/**
 * <recipe-grid> replaces server-side pagination with a client-side equivalent so a
 * grid of blog cards can be filtered by arbitrary card data attributes — used by both
 * the Recipe grid (Product/Type metaobject fields) and the Events/Category grid
 * (article tags). Blogs have no server-side metafield or cross-path filtering that
 * works via a background fetch (see recipe-filter-bar.js), so sections/recipe-grid.liquid
 * and sections/events-grid.liquid both render every matching article in one request and
 * this element shows/hides `.resource-list__item`s in response to the `recipe-filter:change`
 * event dispatched by <recipe-filter-bar>, then paginates the matched set at
 * [data-per-page] items per page.
 *
 * [data-filter-fields] maps URL params to the data-* attribute (camelCase, i.e. the
 * `.dataset` key) on the card marked [data-recipe-grid-card] that holds its comma-wrapped
 * value list, e.g. "flavor:recipeFlavor,type:recipeTypes" or "tag:eventTags".
 */
class RecipeGrid extends HTMLElement {
  connectedCallback() {
    this.perPage = parseInt(this.dataset.perPage, 10) || 12;
    this.currentPage = 1;
    this.filterFields = this._parseFilterFields(this.dataset.filterFields);

    this.list = this.querySelector('[data-recipe-grid-list]');
    this.paginationMount = this.querySelector('[data-recipe-grid-pagination]');
    this.emptyState = this.querySelector('[data-recipe-grid-empty]');
    this.items = this.list ? Array.from(this.list.querySelectorAll('.resource-list__item')) : [];

    this._handleFilterChange = this._handleFilterChange.bind(this);
    document.addEventListener('recipe-filter:change', this._handleFilterChange);

    const params = new URL(window.location.href).searchParams;
    const initialFilters = {};
    this.filterFields.forEach(({ param }) => {
      initialFilters[param] = params.get(param) || '';
    });
    this._applyFilter(initialFilters);
  }

  disconnectedCallback() {
    document.removeEventListener('recipe-filter:change', this._handleFilterChange);
  }

  _parseFilterFields(raw) {
    if (!raw) return [];
    return raw
      .split(',')
      .map((pair) => {
        const [param, dataset] = pair.split(':').map((part) => part.trim());
        return param && dataset ? { param, dataset } : null;
      })
      .filter(Boolean);
  }

  _handleFilterChange(event) {
    this.currentPage = 1;
    this._applyFilter(event.detail || {});
  }

  _applyFilter(filters) {
    this._filters = filters;
    this.matched = this._matchItems(this.filterFields, filters);
    this._renderPage();
    this._dispatchAvailableOptions(filters);
  }

  /**
   * Items whose card satisfies every {param, dataset} field's active filter value.
   * @param {{param: string, dataset: string}[]} fields
   * @param {Record<string, string>} filters
   */
  _matchItems(fields, filters) {
    const activeEntries = fields
      .map(({ param, dataset }) => ({ dataset, value: filters[param] || '' }))
      .filter((entry) => entry.value);

    const hasFilter = activeEntries.length > 0;

    return this.items.filter((item) => {
      if (item.hasAttribute('data-recipe-newsletter')) {
        return !hasFilter;
      }

      const card = item.querySelector('[data-recipe-grid-card]');
      if (!card) return false;

      return activeEntries.every(({ dataset, value }) => {
        const raw = card.dataset[dataset] || '';
        return raw.includes(',' + value + ',');
      });
    });
  }

  /**
   * For each filter field, figures out which option values still have at least one
   * match once every *other* active filter is applied (its own current selection is
   * excluded so its own dropdown doesn't collapse to just the picked value), and
   * dispatches them so <recipe-filter-bar> can hide options that would return nothing —
   * e.g. picking a Flavor narrows the Type dropdown to types that flavor actually has.
   * With only one filter field (eg. Events' single Category dropdown) "every other
   * active filter" is an empty set, so this still runs and simply reports which values
   * exist among the currently-rendered items (eg. narrowed server-side to a /tagged/
   * URL's matches) instead of listing every tag the blog has ever used.
   */
  _dispatchAvailableOptions(filters) {
    const detail = {};

    this.filterFields.forEach(({ param, dataset }) => {
      const otherFields = this.filterFields.filter((field) => field.param !== param);
      const itemsForField = this._matchItems(otherFields, filters);

      const values = new Set();
      itemsForField.forEach((item) => {
        if (item.hasAttribute('data-recipe-newsletter')) return;
        const card = item.querySelector('[data-recipe-grid-card]');
        if (!card) return;
        (card.dataset[dataset] || '')
          .split(',')
          .map((token) => token.trim())
          .filter(Boolean)
          .forEach((token) => values.add(token));
      });

      detail[param] = Array.from(values);
    });

    document.dispatchEvent(new CustomEvent('recipe-grid:options-update', { detail }));
  }

  _renderPage() {
    const totalPages = Math.max(1, Math.ceil(this.matched.length / this.perPage));
    this.currentPage = Math.min(this.currentPage, totalPages);

    const start = (this.currentPage - 1) * this.perPage;
    const visible = new Set(this.matched.slice(start, start + this.perPage));

    this.items.forEach((item) => {
      item.hidden = !visible.has(item);
    });

    if (this.emptyState) {
      this.emptyState.hidden = this.matched.length > 0;
    }

    this._renderPagination(totalPages);
  }

  _renderPagination(totalPages) {
    if (!this.paginationMount) return;
    this.paginationMount.innerHTML = '';
    if (totalPages <= 1) return;

    const nav = document.createElement('nav');
    nav.className = 'pagination pagination--collection';
    nav.setAttribute('aria-label', 'Pagination');

    const list = document.createElement('ul');
    list.className = 'pagination__list';
    list.setAttribute('role', 'list');
    nav.appendChild(list);

    const goTo = (page) => {
      this.currentPage = page;
      this._renderPage();
      this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    list.appendChild(this._arrowItem('Previous', this.currentPage === 1, () => goTo(this.currentPage - 1)));

    for (let page = 1; page <= totalPages; page += 1) {
      list.appendChild(this._pageItem(page, page === this.currentPage, goTo));
    }

    list.appendChild(this._arrowItem('Next', this.currentPage === totalPages, () => goTo(this.currentPage + 1)));

    this.paginationMount.appendChild(nav);
  }

  _arrowItem(label, disabled, onClick) {
    const li = document.createElement('li');
    li.className = 'pagination__item';

    if (disabled) {
      const span = document.createElement('span');
      span.className = 'pagination__link pagination__link--arrow pagination__link--disabled';
      span.setAttribute('aria-label', label);
      span.setAttribute('aria-disabled', 'true');
      span.innerHTML = RecipeGrid.ARROW_SVG;
      li.appendChild(span);
      return li;
    }

    const a = document.createElement('a');
    a.href = '#';
    a.className = 'pagination__link pagination__link--arrow';
    a.setAttribute('aria-label', label);
    a.innerHTML = RecipeGrid.ARROW_SVG;
    a.addEventListener('click', (event) => {
      event.preventDefault();
      onClick();
    });
    li.appendChild(a);
    return li;
  }

  _pageItem(page, isCurrent, goTo) {
    const li = document.createElement('li');
    li.className = 'pagination__item';

    if (isCurrent) {
      const span = document.createElement('span');
      span.className = 'pagination__link pagination__link--page pagination__link--current';
      span.setAttribute('aria-current', 'page');
      span.textContent = String(page);
      li.appendChild(span);
      return li;
    }

    const a = document.createElement('a');
    a.href = '#';
    a.className = 'pagination__link pagination__link--page';
    a.setAttribute('aria-label', `Page ${page}`);
    a.textContent = String(page);
    a.addEventListener('click', (event) => {
      event.preventDefault();
      goTo(page);
    });
    li.appendChild(a);
    return li;
  }
}

RecipeGrid.ARROW_SVG =
  '<svg width="13" height="10" viewBox="0 0 13 10" fill="none" aria-hidden="true"><path d="M8 1L12 5L8 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><line x1="12" y1="5" x2="1" y2="5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';

if (!customElements.get('recipe-grid')) {
  customElements.define('recipe-grid', RecipeGrid);
}
